import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Drawer, Skeleton } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import * as yup from "yup";
import { useFormik } from "formik";
import moment from "moment";
import CustomButton from "shared/components/customButton";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getDemoRequestByIdService,
  reviewDemoRequestService,
} from "shared/services/adminService";
import styles from "./style.module.scss";

const statusConfig: Record<number, { label: string; color: string }> = {
  0: { label: "New", color: "#2196f3" },
  1: { label: "Pending", color: "#ff9800" },
  2: { label: "Approved", color: "#4caf50" },
  3: { label: "Rejected", color: "#f44336" },
  4: { label: "Expired", color: "#9e9e9e" },
  5: { label: "Active", color: "#4caf50" },
  6: { label: "Inactive", color: "#9e9e9e" },
};

function DemoRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<any>(null);
  const [pageLoader, setPageLoader] = useState<boolean>(true);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [statusUpdating, setStatusUpdating] = useState<boolean>(false);
  const [openNotes, setOpenNotes] = useState<boolean>(false);
  const [notesValue, setNotesValue] = useState<string>("");
  const [notesLoader, setNotesLoader] = useState<boolean>(false);

  useBreadcrumbs(
    {
      title: item ? `${item.first_name} ${item.last_name}` : "Demo Request Detail",
      path: routeConstant.demoRequestDetail.path.replace(":id", id || ""),
    },
    true,
    item,
  );

  const fetchDetail = () => {
    setPageLoader(true);
    getDemoRequestByIdService(Number(id))
      .then(({ data }) => {
        const record = data?.data;
        setItem(record);
        setNotesValue(record?.notes ?? "");
        setPageLoader(false);
        if (record?.status === 0) {
          setStatusUpdating(true);
          const formData = new FormData();
          formData.append("status", "1");
          reviewDemoRequestService(Number(id), formData)
            .then(() => {
              setItem((prev: any) => ({ ...prev, status: 1 }));
              setStatusUpdating(false);
            })
            .catch(() => {
              setStatusUpdating(false);
            });
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setPageLoader(false);
      });
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line
  }, [id]);

  const validationSchema = yup.object().shape({
    students_count: yup
      .number()
      .typeError("Must be a valid number")
      .required("Students count is required")
      .min(0, "Cannot be negative"),
    teachers_count: yup
      .number()
      .typeError("Must be a valid number")
      .required("Teachers count is required")
      .min(0, "Cannot be negative"),
    is_multi_campus: yup.string().optional(),
    campuses_count: yup.number().when("is_multi_campus", {
      is: "1",
      then: (schema) =>
        schema
          .typeError("Must be a valid number")
          .required("Number of campuses is required")
          .min(1, "Must be at least 1"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      students_count: item?.students_count ?? "",
      teachers_count: item?.teachers_count ?? "",
      is_multi_campus:
        item?.is_multi_campus != null
          ? item.is_multi_campus
            ? "1"
            : "0"
          : "",
      campuses_count: item?.campuses_count ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("students_count", values.students_count.toString());
      formData.append("teachers_count", values.teachers_count.toString());
      if (values.is_multi_campus)
        formData.append("is_multi_campus", values.is_multi_campus);
      if (values.is_multi_campus === "1")
        formData.append("campuses_count", values.campuses_count.toString());

      setSubmitLoader(true);
      reviewDemoRequestService(Number(id), formData)
        .then(({ data }) => {
          toastMessage("success", data?.message);
          setSubmitLoader(false);
          fetchDetail();
          formik.resetForm();
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          setSubmitLoader(false);
        });
    },
  });

  const handleNotesSubmit = () => {
    const formData = new FormData();
    formData.append("notes", notesValue);
    setNotesLoader(true);
    reviewDemoRequestService(Number(id), formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setNotesLoader(false);
        setItem((prev: any) => ({ ...prev, notes: notesValue }));
        setOpenNotes(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setNotesLoader(false);
      });
  };

  const status = statusConfig[item?.status];

  if (pageLoader) {
    return (
      <div>
        <Box className={styles.infoCard}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "16px" }}>
            <Skeleton variant="circular" width={56} height={56} />
            <div>
              <Skeleton variant="rectangular" width={200} height={22} sx={{ borderRadius: "4px", marginBottom: "10px" }} />
              <div style={{ display: "flex", gap: "40px" }}>
                <Skeleton variant="rectangular" width={100} height={14} sx={{ borderRadius: "4px" }} />
                <Skeleton variant="rectangular" width={100} height={14} sx={{ borderRadius: "4px" }} />
              </div>
            </div>
          </div>
          <hr className={styles.divider} />
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" width={120} height={16} sx={{ borderRadius: "4px" }} />
            ))}
          </div>
        </Box>
        <Box className={styles.formCard}>
          <Skeleton variant="rectangular" width={160} height={22} sx={{ borderRadius: "4px", marginBottom: "20px" }} />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.fieldWrapper}>
              <Skeleton variant="rectangular" width={120} height={16} sx={{ borderRadius: "4px", marginBottom: "8px" }} />
              <Skeleton variant="rectangular" height={42} sx={{ borderRadius: "20px" }} />
            </div>
          ))}
        </Box>
      </div>
    );
  }

  return (
    <div>
      {/* Info Section */}
      <Box className={styles.infoCard}>
        <div className={styles.infoTop}>
          <div className={styles.infoLeft}>
            <div className={styles.avatarCircle}>
              <PersonIcon />
            </div>
            <div className={styles.infoMeta}>
              <div className={styles.name}>
                {item?.first_name} {item?.last_name}
              </div>
              <div className={styles.metaRow}>
                <div className={styles.metaCol}>
                  <span className={styles.metaLabel}>School</span>
                  <span className={styles.metaValue}>{item?.school_name || "—"}</span>
                </div>
                <div className={styles.metaCol}>
                  <span className={styles.metaLabel}>Role</span>
                  <span className={styles.metaValue}>{item?.role || "—"}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.infoActions}>
            <button
              className={styles.notesBtn}
              onClick={() => setOpenNotes(true)}
            >
              {item?.notes ? "Edit Notes" : "Add Notes"}
            </button>
            {statusUpdating ? (
              <Skeleton
                variant="rectangular"
                width={80}
                height={28}
                sx={{ borderRadius: "12px" }}
              />
            ) : (
              status && (
                <span
                  className={styles.statusChip}
                  style={{ backgroundColor: status.color }}
                >
                  {status.label}
                </span>
              )
            )}
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.infoIconRow}>
          <div className={styles.infoIconItem}>
            <PhoneIcon className={styles.infoIcon} />
            <span>{item?.phone || "—"}</span>
          </div>
          <div className={styles.infoIconItem}>
            <BusinessIcon className={styles.infoIcon} />
            <span>{item?.school_name || "—"}</span>
          </div>
          <div className={styles.infoIconItem}>
            <EmailIcon className={styles.infoIcon} />
            <span>{item?.email || "—"}</span>
          </div>
          <div className={styles.infoIconItem}>
            <GroupsIcon className={styles.infoIcon} />
            <span>{item?.students_count ?? "—"} students</span>
          </div>
          <div className={styles.infoIconItem}>
            <PersonOutlineIcon className={styles.infoIcon} />
            <span>{item?.teachers_count ?? "—"} teachers</span>
          </div>
          <div className={styles.infoIconItem}>
            <CalendarTodayIcon className={styles.infoIcon} />
            <span>
              {item?.created_at
                ? moment(item.created_at).format("MMM DD, YYYY")
                : "—"}
            </span>
          </div>
        </div>
      </Box>

      {/* Form Section */}
      <Box className={styles.formCard}>
        <div className={styles.formTitle}>Review Request</div>
        <form onSubmit={formik.handleSubmit}>
          <div className={styles.twoColRow}>
            <div className={styles.fieldWrapper}>
              <label className={styles.label}>Students Count</label>
              <input
                type="number"
                className={styles.inputField}
                id="students_count"
                name="students_count"
                value={formik.values.students_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={0}
              />
              {formik.touched.students_count && formik.errors.students_count && (
                <div className={styles.errorText}>
                  {formik.errors.students_count as string}
                </div>
              )}
            </div>

            <div className={styles.fieldWrapper}>
              <label className={styles.label}>Teachers Count</label>
              <input
                type="number"
                className={styles.inputField}
                id="teachers_count"
                name="teachers_count"
                value={formik.values.teachers_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={0}
              />
              {formik.touched.teachers_count && formik.errors.teachers_count && (
                <div className={styles.errorText}>
                  {formik.errors.teachers_count as string}
                </div>
              )}
            </div>
          </div>

          <div className={formik.values.is_multi_campus === "1" ? styles.twoColRow : ""}>
            <div className={styles.fieldWrapper}>
              <label className={styles.label}>Multi Campus</label>
              <select
                className={styles.selectField}
                id="is_multi_campus"
                name="is_multi_campus"
                value={formik.values.is_multi_campus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select</option>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>

            {formik.values.is_multi_campus === "1" && (
              <div className={styles.fieldWrapper}>
                <label className={styles.label}>No. of Campuses</label>
                <input
                  type="number"
                  className={styles.inputField}
                  id="campuses_count"
                  name="campuses_count"
                  value={formik.values.campuses_count}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={1}
                />
                {formik.touched.campuses_count && formik.errors.campuses_count && (
                  <div className={styles.errorText}>
                    {formik.errors.campuses_count as string}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.btnContainer}>
            <CustomButton
              text="Submit"
              buttonStyle={styles.btnCustom}
              handleClick={formik.handleSubmit}
            />
            <CustomButton
              text="Cancel"
              buttonStyle={styles.btnInsideOut}
              handleClick={() => navigate(routeConstant.demoRequests.path)}
            />
          </div>
        </form>
      </Box>

      {/* Notes Drawer */}
      <Drawer
        anchor="right"
        open={openNotes}
        onClose={() => setOpenNotes(false)}
      >
        <div className={styles.drawerContainer}>
          <div className={styles.drawerHeader}>
            <span className={styles.drawerTitle}>Notes</span>
            <button
              className={styles.drawerClose}
              onClick={() => setOpenNotes(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={styles.drawerBody}>
            <label className={styles.label}>Notes</label>
            <textarea
              className={styles.textarea}
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              placeholder="Add notes here..."
            />
          </div>

          <div className={styles.drawerFooter}>
            <CustomButton
              text="Submit"
              buttonStyle={styles.btnCustom}
              handleClick={handleNotesSubmit}
            />
            <CustomButton
              text="Cancel"
              buttonStyle={styles.btnInsideOut}
              handleClick={() => setOpenNotes(false)}
            />
          </div>
        </div>
      </Drawer>

      {(submitLoader || notesLoader) && <Loader />}
    </div>
  );
}

export default DemoRequestDetail;
